import { useState, useEffect, useCallback } from 'react';
import { ProviderConfig, ProviderKind } from '../types';
import { useChat } from '../utils/chatContext';
import { command } from '../utils/backend';

interface Props {
  open: boolean;
  onClose: () => void;
}

const PROVIDER_PRESETS: Record<ProviderKind, { name: string; base_url: string; needsKey: boolean }> = {
  openai:   { name: 'OpenAI',    base_url: 'https://api.openai.com/v1', needsKey: true },
  ollama:   { name: 'Ollama',    base_url: 'http://localhost:11434/v1', needsKey: false },
  vllm:     { name: 'VLLM',      base_url: 'http://localhost:8000/v1',  needsKey: false },
  lmstudio: { name: 'LM Studio', base_url: 'http://localhost:1234/v1',  needsKey: false },
  custom:   { name: 'Custom',    base_url: '',                          needsKey: false },
};

const PROVIDER_OPTIONS: { value: ProviderKind; label: string }[] = [
  { value: 'ollama',   label: 'Ollama' },
  { value: 'openai',   label: 'OpenAI' },
  { value: 'vllm',     label: 'VLLM' },
  { value: 'lmstudio', label: 'LM Studio' },
  { value: 'custom',   label: 'Custom' },
];

export function SettingsModal({ open, onClose }: Props) {
  const { config, setConfig } = useChat();

  const [kind, setKind] = useState<ProviderKind>('ollama');
  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Model list state
  const [models, setModels] = useState<string[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState('');

  // Populate form from current config
  useEffect(() => {
    if (config && open) {
      setKind(config.kind);
      setName(config.name);
      setBaseUrl(config.base_url);
      setApiKey(config.api_key ?? '');
      setModel(config.model);
      setTemperature(config.temperature);
      setMaxTokens(config.max_tokens?.toString() ?? '');
      setError('');
      setModelsError('');
    }
  }, [config, open]);

  const fetchModels = useCallback(async (url?: string, key?: string) => {
    const targetUrl = url ?? baseUrl;
    if (!targetUrl.trim()) {
      setModels([]);
      return;
    }

    setModelsLoading(true);
    setModelsError('');

    try {
      const result = await command<string[]>('list_models', {
        baseUrl: targetUrl.replace(/\/+$/, ''),
        apiKey: (key ?? apiKey).trim() || null,
      });
      setModels(result);
      // If current model isn't in the list and we got results, auto-select first
      if (result.length > 0 && !result.includes(model)) {
        setModel(result[0]);
      }
    } catch (e) {
      setModelsError(String(e));
      setModels([]);
    } finally {
      setModelsLoading(false);
    }
  }, [baseUrl, apiKey, model]);

  // Fetch models when modal opens (if we have a URL)
  useEffect(() => {
    if (open && baseUrl.trim()) {
      fetchModels();
    }
  }, [open]);

  const handleKindChange = (newKind: ProviderKind) => {
    setKind(newKind);
    const preset = PROVIDER_PRESETS[newKind];
    setName(preset.name);
    setBaseUrl(preset.base_url);
    setModels([]);
    setModelsError('');
    if (!preset.needsKey) setApiKey('');
    // Auto-fetch models for the new preset URL
    if (preset.base_url) {
      fetchModels(preset.base_url, preset.needsKey ? apiKey : '');
    }
  };

  const handleSave = async () => {
    if (!baseUrl.trim()) { setError('Base URL is required'); return; }
    if (!model.trim()) { setError('Model name is required'); return; }

    setSaving(true);
    setError('');

    try {
      const newConfig: ProviderConfig = {
        kind,
        name: name || PROVIDER_PRESETS[kind].name,
        base_url: baseUrl.replace(/\/+$/, ''),
        api_key: apiKey.trim() || null,
        model: model.trim(),
        temperature,
        max_tokens: maxTokens ? parseInt(maxTokens, 10) : null,
      };
      await setConfig(newConfig);
      onClose();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {/* Provider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
            <select
              value={kind}
              onChange={e => handleKindChange(e.target.value as ProviderKind)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              {PROVIDER_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. My Ollama Server"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Base URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Base URL</label>
            <input
              type="text"
              value={baseUrl}
              onChange={e => setBaseUrl(e.target.value)}
              placeholder="http://localhost:11434/v1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
            />
            <p className="text-xs text-gray-400 mt-1">OpenAI-compatible /v1 endpoint</p>
          </div>

          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key {!PROVIDER_PRESETS[kind].needsKey && <span className="text-gray-400">(optional)</span>}
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder={PROVIDER_PRESETS[kind].needsKey ? 'sk-...' : 'Leave blank if not required'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
            />
          </div>

          {/* Fetch Models Button */}
          <div>
            <button
              onClick={() => fetchModels()}
              disabled={!baseUrl.trim() || modelsLoading}
              className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {modelsLoading ? 'Fetching...' : 'Fetch Available Models'}
            </button>
            {modelsError && (
              <p className="text-xs text-red-500 mt-1">{modelsError}</p>
            )}
          </div>

          {/* Model */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
            {models.length > 0 ? (
              <select
                value={model}
                onChange={e => setModel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
              >
                {models.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={model}
                onChange={e => setModel(e.target.value)}
                placeholder="e.g. llama3, gpt-4o, mistral"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
              />
            )}
            {models.length > 0 && (
              <p className="text-xs text-gray-400 mt-1">{models.length} model{models.length !== 1 ? 's' : ''} available</p>
            )}
          </div>

          {/* Temperature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Temperature: {temperature.toFixed(1)}
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={temperature}
              onChange={e => setTemperature(parseFloat(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>Precise (0)</span>
              <span>Creative (2)</span>
            </div>
          </div>

          {/* Max Tokens */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Tokens <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="number"
              value={maxTokens}
              onChange={e => setMaxTokens(e.target.value)}
              placeholder="Leave blank for model default"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
